import React, { useState, useEffect, useRef } from 'react';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dunpaiSvg from '@/assets/dunpai.svg';

const TypingBubble = ({ fullText, historyData, onTypingComplete }) => {
  const themeMode = localStorage.getItem('themeMode') ?? 'light';
  const COT_MARKER = 'cotComplete';
  const [displayText, setDisplayText] = useState('');
  const indexRef = useRef(0);
  const segmentRef = useRef('');
  const [forceComplete, setForceComplete] = useState(false);
  const { t } = useTranslation('chat');

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setForceComplete(true);
      }
      if (document.visibilityState === 'visible') {
        if (segmentRef.current) {
          setDisplayText(segmentRef.current.replace(/#/g, ' '));
          indexRef.current = segmentRef.current.length;
        }
        setForceComplete(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!fullText) return;

    const isLastSegment = fullText.endsWith(COT_MARKER);
    if (isLastSegment) onTypingComplete?.();
    const newSegment = isLastSegment ? fullText.slice(0, -COT_MARKER.length) : fullText;

    if (!newSegment || historyData || forceComplete) {
      setDisplayText(newSegment.replace(/#/g, ' '));
      indexRef.current = newSegment.length;
      segmentRef.current = newSegment;
      return;
    }

    if (newSegment !== segmentRef.current) {
      segmentRef.current = newSegment;
      indexRef.current = displayText.length;
    }

    let isCancelled = false;

    const typeNext = () => {
      if (isCancelled) return;

      let nextChar = segmentRef.current[indexRef.current];
      if (nextChar === undefined) {
        setTimeout(typeNext, 50);
        return;
      }

      if (nextChar === '#') {
        nextChar = ' ';
      }

      setDisplayText(prev => prev + nextChar);
      indexRef.current += 1;

      if (indexRef.current < segmentRef.current.length) {
        setTimeout(typeNext, 5);
      }
    };

    typeNext();

    return () => {
      isCancelled = true;
    };
  }, [forceComplete, fullText, historyData, onTypingComplete]);

  const translatedBoldPhrases = t('deepTrace.boldPhrases', { returnObjects: true });
  const boldPhrases = Array.isArray(translatedBoldPhrases) ? translatedBoldPhrases : [];

  const renderEmphasizedText = (text, keyPrefix) => {
    if (!text) return null;

    if (boldPhrases.length === 0) {
      return <React.Fragment key={`${keyPrefix}-text-0`}>{text}</React.Fragment>;
    }

    const escapedPhrases = boldPhrases.map(phrase => phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escapedPhrases.join('|')})`, 'g');
    const parts = String(text).split(pattern);

    return parts.map((part, index) => {
      if (boldPhrases.includes(part)) {
        return (
          <strong key={`${keyPrefix}-bold-${index}`} style={{ fontWeight: 700 }}>
            {part}
          </strong>
        );
      }
      return <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>;
    });
  };

  const renderLineContent = (line, lineIndex) => {
    const parts = String(line).split(/(SHIELD_ICON)/);
    return parts.map((part, partIndex) => {
      if (part === 'SHIELD_ICON') {
        return (
          <Tooltip
            color="#999999"
            key={`${lineIndex}-${partIndex}`}
            title={t('riskBubble.pii')}
            overlayStyle={{ maxWidth: '600px', whiteSpace: 'normal' }}
          >
            <span className="inline-icon-wrapper">
              <img
                src={dunpaiSvg}
                alt="shield"
                className="inline-icon-img"
                data-testid="scams-report-thought-chain-shield-icon"
              />
            </span>
          </Tooltip>
        );
      }
      return (
        <React.Fragment key={`${lineIndex}-${partIndex}`}>
          {renderEmphasizedText(part, `${lineIndex}-${partIndex}`)}
        </React.Fragment>
      );
    });
  };

  const lines = displayText.split(/(\|)/);

  return (
    <div style={{ color: themeMode === 'light' ? '#8B8B8B' : '#A6A6A6' }}>
      {lines.map((line, i) => {
        if (line === '|') {
          return <div key={i} style={{ marginBottom: '10px' }} />;
        }

        return (
          <div key={i} style={{ lineHeight: '1.5' }}>
            {renderLineContent(line, i)}
          </div>
        );
      })}
    </div>
  );
};

export default TypingBubble;
